import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { MembershipPlanProps } from 'components/crossSell/PlanType/planType.types';
import { DoctorStateProps } from 'components/crossSell/SelectDoctorCard/selectDoctorCard.types';
import {
  CreateAccountResponse,
  GetAvailableDoctorsProps,
  PatientAppointmentType
} from 'store/crossSell/crossSell.types';
import { CreatePatientProps } from 'store/patients/patients.types';

export const crossSellApi = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_PATIENT_URL,
    prepareHeaders: (headers) => {
      headers.set('API-KEY', import.meta.env.VITE_API_KEY ?? '');
      headers.set('Platform', 'web-physician');
      return headers;
    }
  }),
  endpoints: (build) => ({
    createAccount: build.mutation<
      CreateAccountResponse,
      CreatePatientProps & { accessToken?: string; idempotencyKey?: string }
    >({
      query: ({ accessToken, idempotencyKey, ...body }) => ({
        body,
        headers: {
          // Lets the backend reject a duplicate copy of this submit instead of creating a second account.
          ...(idempotencyKey && { 'Idempotency-Key': idempotencyKey }),
          /*
           * The patient session a staff member acquired on the account being converted. Without it this
           * submit is anonymous, the backend has no id to resume by, and it falls back to claiming the
           * account by the email in the form — which it refuses for a lead or for anyone who already set a
           * password, surfacing as "User already exists".
           *
           * Raw, NOT `Bearer`-prefixed: the patient portal hands the header value straight to `jwt.verify`.
           */
          ...(accessToken && { Authorization: accessToken })
        },
        method: 'POST',
        url: `/auth/orchestrate-signup`
      }),
      transformResponse: (response: { data: CreateAccountResponse }) => response.data
    }),

    getAvailableDoctors: build.query<
      { data: DoctorStateProps[]; info: { totalCount: number } },
      GetAvailableDoctorsProps
    >({
      keepUnusedDataFor: 3,
      query: ({ isInitialVisit, isUnlimitedPlan, limit, pageNo, state }) => ({
        params: { isInitialVisit, isUnlimitedPlan, limit, pageNo, state },
        url: `/auth/available-doctors`
      }),
      transformResponse: (response: {
        data: DoctorStateProps[];
        info: { totalCount: number };
      }) => ({
        data: response.data,
        info: response.info
      })
    }),

    getDoctors: build.query({
      keepUnusedDataFor: 3,
      query: ({
        appointmentTypeId,
        date,
        isInitialVisit,
        isUnlimitedPlan,
        itemsCount: limit,
        onboardingPartnerName,
        page: pageNo,
        planId,
        state,
        timezone
      }) => ({
        params: {
          appointmentTypeId,
          date,
          isInitialVisit,
          isUnlimitedPlan,
          limit,
          onboardingPartnerName,
          pageNo,
          planId,
          state,
          timezone
        },
        url: `/auth/doctors`
      }),
      transformResponse: (response: {
        data: DoctorStateProps[];
        info: {
          totalCount: number;
        };
      }) => ({
        data: response.data,
        info: response.info
      })
    }),

    getMembershipPlans: build.query<MembershipPlanProps[], { attFilter?: boolean } | void>({
      query: (params) => ({
        params: {
          limit: 50,
          ...(params?.attFilter ? { attFilter: 'true' } : {})
        },
        url: '/lookup/membership-plans'
      }),
      transformResponse: (response: { data: MembershipPlanProps[] }) => response.data
    }),

    getPatientAppointmentTypes: build.query<PatientAppointmentType[], { params: string }>({
      query: ({ params }) => `/lookup/appointment-types?${params}`,
      transformResponse: (response: { data: PatientAppointmentType[] }) => response.data
    }),
    getStates: build.query<{ label: string; value: string }[], void>({
      query: () => '/lookup/state-list',
      transformResponse: (response: { data: { name: string; stateCode: string }[] }) => {
        return response.data.map((v) => ({
          label: v.name,
          value: v.stateCode
        }));
      }
    }),

    /**
     * Redeems a staff-minted `patientViewToken` for a patient session on the account being converted.
     *
     * The second half of the staff handoff: the physician portal mints the token on a staff-authenticated
     * route, and this exchanges it here, on the patient portal, for an access token carrying `isStaff` and
     * `staffId`. That token is what makes the conversion submit a resume-by-id rather than an anonymous
     * email claim.
     */
    staffPatientLogin: build.mutation<
      { _id: string; accessToken: string },
      { loginToken: string; staffId: string }
    >({
      query: ({ loginToken, staffId }) => ({
        method: 'POST',
        params: { loginToken, staffId },
        url: '/auth/staff/patient-login'
      }),
      transformResponse: (response: { data: { _id: string; accessToken: string } }) => response.data
    })
  }),
  reducerPath: 'crossSellApi'
});

export const {
  useCreateAccountMutation,
  useGetMembershipPlansQuery,
  useGetStatesQuery,
  useLazyGetAvailableDoctorsQuery,
  useLazyGetDoctorsQuery,
  useLazyGetMembershipPlansQuery,
  useLazyGetPatientAppointmentTypesQuery,
  useStaffPatientLoginMutation
} = crossSellApi;
